import React from 'react';
import { Edit, Trash2, Package, AlertTriangle, CheckCircle, ChefHat, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { useProductCategories } from '../hooks/useProductCategories';
import { Product } from '../types/product.types';

interface ProductTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (productId: string) => void;
  onManagePresentations: (product: Product) => void;
  onViewRecipes: (product: Product) => void;
  onManageLocations: (product: Product) => void;
}

type SortField = 'name' | 'categoryId' | 'priceRange' | 'presentationCount' | 'status';

export const ProductTable: React.FC<ProductTableProps> = ({ products, onEdit, onDelete, onManagePresentations, onViewRecipes, onManageLocations }) => {
  const { categories } = useProductCategories();

  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    name: 'Producto',
    categoryId: 'Categoría',
    priceRange: 'Rango de Precios',
    presentationCount: 'Presentaciones',
    status: 'Estado'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (product: Product, field: SortField): any => {
    switch (field) {
      case 'name':
        return product.name.toLowerCase();
      case 'categoryId':
        return getCategoryName(product.categoryId).toLowerCase();
      case 'priceRange':
        const activePresentations = product.presentations?.filter(p => p.isActive) || [];
        if (activePresentations.length === 0) return 0;
        const prices = activePresentations.map(p => p.price);
        return Math.min(...prices);
      case 'presentationCount':
        return product.presentations?.length || 0;
      case 'status':
        const hasActive = (product.presentations?.filter(p => p.isActive) || []).length > 0;
        return hasActive ? 1 : 0;
      default:
        return product.name;
    }
  };

  // Usar el hook reutilizable
  const tableData = useTableData<Product, SortField>(
    {
      data: products,
      defaultSortField: 'name',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  // Function to get category name from categoryId (UUID)
  const getCategoryName = (categoryId: string) => {
    console.log('🎯 getCategoryName: Looking for categoryId=', categoryId, 'in categories=', categories);
    const category = categories.find(cat => cat.id === categoryId);
    console.log('🔍 getCategoryName: Found category=', category);
    return category ? category.name : 'Sin categoría'; // Fallback if category not found
  };
  const getPriceRange = (product: Product) => {
    if (!product.presentations || product.presentations.length === 0) {
      return 'Sin presentaciones';
    }
    
    const activePresentations = product.presentations.filter(p => p.isActive);
    if (activePresentations.length === 0) {
      return 'Sin presentaciones activas';
    }
    
    const prices = activePresentations.map(p => p.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    if (minPrice === maxPrice) {
      return `S/ ${minPrice.toFixed(2)}`;
    }
    
    return `S/ ${minPrice.toFixed(2)} - S/ ${maxPrice.toFixed(2)}`;
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    const categoryCode = category?.code || 'otros';
    const colors = {
      sandwiches: 'bg-green-50 text-green-600',
      hamburguesas: 'bg-red-50 text-red-600',
      bebidas: 'bg-blue-50 text-blue-600',
      complementos: 'bg-orange-50 text-orange-600',
      postres: 'bg-purple-50 text-purple-600',
      extras: 'bg-orange-50 text-orange-600',
      otros: 'bg-gray-50 text-gray-600'
    };
    return colors[categoryCode as keyof typeof colors] || colors.otros;
  };

  const getStatusBadge = (product: Product) => {
    if (product.isActive === false) {
      return {
        label: 'Inactivo',
        color: 'bg-red-100 text-red-700',
        icon: <AlertTriangle className="w-4 h-4 text-red-500" />
      };
    }
    return {
      label: 'Activo',
      color: 'bg-green-100 text-green-700',
      icon: <CheckCircle className="w-4 h-4 text-green-500" />
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  if (products.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay productos</h3>
        <p className="text-gray-500">No se encontraron productos con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Producto
              </SortableHeader>
              <SortableHeader
                field="categoryId"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Categoría
              </SortableHeader>
              <SortableHeader
                field="priceRange"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Rango de Precios
              </SortableHeader>
              <SortableHeader
                field="presentationCount"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Presentaciones
              </SortableHeader>
              <SortableHeader
                field="status"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Estado
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((product) => {
              const status = getStatusBadge(product);
              const presentationsCount = product.presentations?.length || 0;
              
              return (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 mr-3">
                        {status.icon}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {product.name}
                        </div>
                        {product.description && (
                          <div className="text-sm text-gray-500">
                            {product.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-4 py-4">
                    <Badge
                      variant="outline"
                      className={`${getCategoryColor(product.categoryId)} border-0`}
                    >
                      {getCategoryName(product.categoryId)}
                    </Badge>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm font-medium text-gray-900">
                      {getPriceRange(product)}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {presentationsCount} {presentationsCount === 1 ? 'presentación' : 'presentaciones'}
                    </div>
                    {presentationsCount > 0 && (
                      <div className="text-xs text-gray-500">
                        {product.presentations?.filter(p => p.isActive).length || 0} activas
                      </div>
                    )}
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <Badge className={status.color}>
                      {status.label}
                    </Badge>
                  </td>
                  
                  <td className="px-4 py-4">
                    <div className="flex gap-2 justify-center flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(product)}
                        className="text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onManagePresentations(product)}
                        className="text-xs"
                      >
                        <Package className="w-3 h-3 mr-1" />
                        Presentaciones
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewRecipes(product)}
                        className="text-xs"
                      >
                        <ChefHat className="w-3 h-3 mr-1" />
                        Recetario
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onManageLocations(product)}
                        className="text-xs"
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        Local
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(product.id)}
                        className={`text-xs ${product.isActive ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                      >
                        {product.isActive ? (
                          <><Trash2 className="w-3 h-3 mr-1" /> Desactivar</>
                        ) : (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Activar</>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};